import React from "react";
import { matchPath, Navigate, useLocation, useNavigate } from "react-router-dom";
import { getAndClearRedirectUrl } from "@/shared/utils/redirectHandlers";
import { useAuth } from "@/shared/stores/useAuth";
import { User } from "@/shared/types";
import { ScreenLoader } from "@/shared/components";
import { useGetUserProfile } from "@/shared/api/user.api";
import { useErrorResponse } from "@/shared/hooks";
import { storeFanCardRedirectFromUrl } from "@/shared/utils/fanCardCollectionRedirects";
import { ROUTES } from "@/shared/constants";

const userRoutePattern = "user/:id";

/** Same-origin path only — avoids open redirects from router state. */
function getSafePostAuthPathFromState(state: unknown): string | null {
  if (!state || typeof state !== "object") return null;
  const redirect = (state as { redirect?: unknown }).redirect;
  if (typeof redirect !== "string" || !redirect.startsWith("/")) return null;
  if (redirect.startsWith("//")) return null;
  if (redirect.includes("://")) return null;
  return redirect;
}

interface WithAuthenticationProps {
  children?: React.ReactNode;
}

const withAuthentication = (
  WrappedComponent: React.ComponentType<WithAuthenticationProps>,
  allowedRoutes: string[] = [],
): React.ComponentType<WithAuthenticationProps> => {
  return (props: WithAuthenticationProps) => {
    const { userInfo, setUser, sessionInvalidated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const {
      data: userQuery,
      error,
      isLoading: userLoading,
    } = useGetUserProfile({
      key: ["user-profile"],
      options: { enabled: false },
    });

    const cachedUser =
      sessionInvalidated || error
        ? null
        : ((userQuery?.data as User | null) ?? null);
    const resolvedUser = userInfo ?? cachedUser;
    const isLoggedIn = Boolean(resolvedUser);

    React.useEffect(() => {
      if (!sessionInvalidated && cachedUser && !userInfo) {
        setUser(cachedUser);
      }
    }, [cachedUser, userInfo, setUser, sessionInvalidated]);

    useErrorResponse(error);
 console.log("user loading", userLoading);
    React.useEffect(() => {
      if (!isLoggedIn) {
        if (userLoading) return;

        const currentPath = window.location.pathname;
        if (allowedRoutes.includes(currentPath)) return;

        const fullUrl = window.location.href;
        if (
          fullUrl.includes("/talent/profile/") &&
          fullUrl.includes("fanCardId=")
        ) {
          storeFanCardRedirectFromUrl(fullUrl);
        }

        navigate(ROUTES.SIGN_IN, {
          replace: true,
          state: {
            redirect: location.pathname + location.search,
          },
        });
        return;
      }

      const currentPath = window.location.pathname;
      const userMatchedRoutePattern = matchPath(userRoutePattern, currentPath);
      const storedRedirect = getAndClearRedirectUrl();

      if (storedRedirect) {
        window.setTimeout(() => {
          window.location.href = storedRedirect;
        }, 100);
        return;
      }

      if (
        currentPath === "/register" ||
        currentPath === "/sign-in" ||
        currentPath === "/guest"
      ) {
        const postAuthPath = getSafePostAuthPathFromState(location.state);
        navigate(postAuthPath ?? "/lobby", { replace: true });
        return;
      }

      if (
        userMatchedRoutePattern?.pattern?.path === userRoutePattern &&
        !["User", "Talent", "Business", "Organization"].includes(
          resolvedUser?.account_type ?? "",
        )
      ) {
        localStorage.clear();
        navigate("/home", { replace: true });
      }
    }, [
      isLoggedIn,
      resolvedUser,
      navigate,
      location.pathname,
      location.state,
      location.search,
      userLoading,
      allowedRoutes,
    ]);

    const isAllowedGuestRoute = allowedRoutes.includes(location.pathname);

    // Logged-in users render immediately — never block on cached profile reads.
    if (userInfo) {
      return <WrappedComponent {...props} />;
    }

    if (!isLoggedIn) {
      if (isAllowedGuestRoute) {
        return <WrappedComponent {...props} />;
      }

      if (userLoading) return <ScreenLoader />;
      return (
        <Navigate
          to={ROUTES.SIGN_IN}
          replace
          state={{ redirect: location.pathname + location.search }}
        />
      );
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAuthentication;
